/**
 * The Probe area's pure logic: how saved interviews are labelled, matched
 * to areas, exported and handed to the chat. The practice picture itself
 * (areas, trends, actions) is `@amoreg/shared/probePractice`, which the
 * agent's practice tool shares.
 *
 * @module components/apps/probe/probeArea
 */
import {
  PROBE_EXAMINATION_AREAS,
  type ProbeAreaAssessment,
  type ProbeBriefingFile,
  type ProbeBriefingItems,
  type ProbeExaminationArea,
  type ProbeOutcome,
} from "@amoreg/contracts";
import { areaLabel, interviewName, reviewActionsOf } from "@amoreg/shared/probePractice";

import type { ProbeInterviewDetail, ProbeInterviewSummary } from "../../../state/probeHistory";
import { interviewSources } from "../../../state/probeHistory";
import { reviewMomentsOf } from "./ProbeReview.logic";

// ── Labels ────────────────────────────────────────────────────────────

export const OUTCOME_LABELS: Readonly<Record<ProbeOutcome, string>> = {
  strong: "Strong",
  mixed: "Mixed",
  weak: "Needs work",
};

export const ASSESSMENT_LABELS: Readonly<Record<ProbeAreaAssessment, string>> = {
  strong: "Strong",
  adequate: "Adequate",
  weak: "Weak",
};

/** An area as a URL segment (`AML/CFT and sanctions` → `aml-cft-and-sanctions`). */
export const areaSlug = (area: ProbeExaminationArea): string =>
  area
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/** The area a slug names, or null for anything else. */
export const areaFromSlug = (slug: string | null | undefined): ProbeExaminationArea | null =>
  slug ? (PROBE_EXAMINATION_AREAS.find((area) => areaSlug(area) === slug) ?? null) : null;

/**
 * What the stage says for the person when they start an interview on one
 * area. It goes to the examiner as their first words — the way someone
 * would steer it by voice — so the call itself is unchanged.
 */
export const focusAsk = (area: ProbeExaminationArea): string =>
  `I'd like to rehearse ${area} today. Please focus your questions there.`;

/** `42 s`, `7 min`, `1 h 05 min`. */
export const formatProbeDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return "";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")} min`;
};

// ── Areas ─────────────────────────────────────────────────────────────

/** Whether an interview examined an area: its debrief rated it, or its review found a moment in it. */
export const interviewCovers = (
  interview: Pick<ProbeInterviewSummary, "debrief" | "review">,
  area: ProbeExaminationArea,
): boolean =>
  (interview.debrief?.areas.some((entry) => entry.area === area) ?? false) ||
  (interview.review?.status === "ready" && interview.review.moments.some((moment) => moment.area === area));

// ── Export and chat ───────────────────────────────────────────────────

const isoDate = (iso: string): string => (Number.isFinite(Date.parse(iso)) ? iso.slice(0, 10) : "");

/** A file name for an interview's export: `probe-2026-10-12-who-signs-off.md`. */
export const interviewFileName = (
  detail: Pick<ProbeInterviewSummary, "title" | "createdAt">,
): string => {
  const words = detail.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48)
    .replace(/-$/, "");
  return ["probe", isoDate(detail.createdAt), words].filter(Boolean).join("-") + ".md";
};

/**
 * The interview as Markdown: what was said, the summary and debrief, and
 * the pages the examiner checked — the export a person files or forwards.
 */
export const interviewMarkdown = (detail: ProbeInterviewDetail): string => {
  const meta = [
    isoDate(detail.createdAt),
    detail.durationMs === null ? null : formatProbeDuration(detail.durationMs),
    detail.debrief === null ? null : `Outcome: ${OUTCOME_LABELS[detail.debrief.outcome]}`,
  ].filter((part): part is string => part !== null && part.length > 0);
  const lines: Array<string> = [
    `# ${interviewName(detail)}`,
    "",
    `Probe rehearsal interview${meta.length > 0 ? ` · ${meta.join(" · ")}` : ""}`,
  ];
  if (detail.summary !== null) lines.push("", "## Summary", "", detail.summary);
  const review = detail.review?.status === "ready" ? detail.review : null;
  if (review !== null) {
    lines.push("", "## Review");
    for (const moment of reviewMomentsOf(review.moments, detail.turns)) {
      lines.push(
        "",
        `### ${areaLabel(moment.area)}`,
        "",
        `> ${moment.said.replace(/\n+/g, " ")}`,
        "",
      );
      lines.push(moment.reason, "", `A strong answer covers: ${moment.strongerAnswer}`);
    }
  }
  if (detail.debrief !== null && detail.debrief.areas.length > 0) {
    lines.push("", "## Areas", "");
    for (const entry of detail.debrief.areas) {
      lines.push(
        `- **${areaLabel(entry.area)}** — ${ASSESSMENT_LABELS[entry.assessment]}${entry.note ? `. ${entry.note}` : ""}`,
      );
    }
  }
  const actions = reviewActionsOf(detail);
  if (actions.length > 0) lines.push("", "## Actions", "", ...actions.map((text) => `- [ ] ${text}`));
  const sources = interviewSources(detail.turns);
  if (sources.length > 0) {
    lines.push(
      "",
      "## Sources",
      "",
      ...sources.map((source) => `- [${source.title}](${source.url})`),
    );
  }
  lines.push("", "## Transcript", "");
  for (const turn of detail.turns) {
    lines.push(`**${turn.role === "assistant" ? "Examiner" : "You"}:** ${turn.text.trim()}`, "");
  }
  return `${lines.join("\n").trimEnd()}\n`;
};

/** The toast Export → Copy ends with. */
export interface CopyInterviewToast {
  readonly type: "success" | "error";
  readonly title: string;
}

/**
 * Copies the interview with `write` and says how it went: copied, or that
 * it was not, also when the clipboard and its fallback both refuse (a
 * rejection), so a failed copy is never silent.
 */
export const copyInterviewToast = (write: () => Promise<boolean>): Promise<CopyInterviewToast> =>
  write().then(
    (copied): CopyInterviewToast =>
      copied
        ? { type: "success", title: "Interview copied as Markdown" }
        : { type: "error", title: "Could not copy the interview" },
    (): CopyInterviewToast => ({ type: "error", title: "Could not copy the interview" }),
  );

/**
 * What asking Amoreg about an interview puts in the chat's composer: the
 * interview by id, so the agent reads the whole thing with its own Probe tool
 * rather than from a pasted copy, and the question asked, or one to start from.
 */
export const askAboutInterviewText = (
  detail: Pick<ProbeInterviewSummary, "id" | "title" | "createdAt">,
  question?: string,
): string => {
  const date = isoDate(detail.createdAt);
  const asked = question?.trim() ?? "";
  return `About my Probe rehearsal "${detail.title}"${date ? ` on ${date}` : ""} (interview ${detail.id}): read it, then ${asked.length > 0 ? `answer: ${asked}` : "tell me where my answers were weakest and what evidence I should have ready before the real examination."}`;
};

// ── The briefing's lines ──────────────────────────────────────────────

/*
 * The briefing file's lines are the examiner's own (`buildProbeCaseFile` on
 * the server): one fact per line, parts joined by " — ". The page splits
 * them only to set the parts apart; a line in any other shape is shown
 * whole, never dropped.
 */

const PART = " — ";

/** `[high] Stale PEP list — the summary` → severity, title, detail. */
export const findingLine = (
  line: string,
): { readonly severity: string | null; readonly title: string; readonly detail: string | null } => {
  const match = /^\[([a-z]+)\]\s+(.*)$/i.exec(line.trim());
  const severity = match ? match[1]!.toLowerCase() : null;
  const rest = match ? match[2]! : line.trim();
  const cut = rest.indexOf(PART);
  return cut === -1
    ? { severity, title: rest, detail: null }
    : { severity, title: rest.slice(0, cut), detail: rest.slice(cut + PART.length) };
};

/** `2026-09-01 — Jersey — Title` → date, where, title (each part only when present). */
export const updateLine = (
  line: string,
): { readonly date: string | null; readonly where: string | null; readonly title: string } => {
  const parts = line.split(PART).map((part) => part.trim());
  const date = parts[0] && /^\d{4}-\d{2}-\d{2}$/.test(parts[0]) ? parts.shift()! : null;
  if (parts.length >= 2) return { date, where: parts[0]!, title: parts.slice(1).join(PART) };
  return { date, where: null, title: parts.join(PART) };
};

/** `Jersey: [source] passage` → jurisdiction, source, text. */
export const authorityLine = (
  line: string,
): {
  readonly jurisdiction: string | null;
  readonly source: string | null;
  readonly text: string;
} => {
  const colon = line.indexOf(": ");
  const jurisdiction = colon > 0 ? line.slice(0, colon) : null;
  const rest = colon > 0 ? line.slice(colon + 2) : line;
  const match = /^\[([^\]]+)\]\s+(.*)$/s.exec(rest);
  return match
    ? { jurisdiction, source: match[1]!, text: match[2]! }
    : { jurisdiction, source: null, text: rest };
};

/** `AML Policy — policy, approved, owner a@b` → name, facts. */
export const libraryLine = (
  line: string,
): { readonly name: string; readonly facts: string | null } => {
  const cut = line.indexOf(PART);
  return cut === -1
    ? { name: line, facts: null }
    : { name: line.slice(0, cut), facts: line.slice(cut + PART.length) };
};

/** A library document's facts, read back from the line the examiner reads (`buildProbeCaseFile`). */
export interface LibraryFacts {
  /** `policy`, `procedure`, or whatever kind the library names. */
  readonly kind: string | null;
  /** Its lifecycle (`published`, `draft`), or `not under governance`. */
  readonly status: string | null;
  /** The owner's email; null with no owner named. */
  readonly owner: string | null;
  /** `YYYY-MM-DD`. */
  readonly reviewDue: string | null;
}

const OWNER = "owner ";
const REVIEW_DUE = "review due ";

export const libraryFacts = (facts: string): LibraryFacts => {
  const parts = facts
    .split(", ")
    .map((part) => part.trim())
    .filter((part) => part !== "");
  const owner = parts.find((part) => part.startsWith(OWNER));
  const due = parts.find((part) => part.startsWith(REVIEW_DUE));
  const plain = parts.filter((part) => part !== owner && part !== due && part !== "no owner");
  return {
    kind: plain[0] ?? null,
    status: plain[1] ?? null,
    owner: owner === undefined ? null : owner.slice(OWNER.length),
    reviewDue: due === undefined ? null : due.slice(REVIEW_DUE.length),
  };
};

/**
 * A person's name from their email, when its local part is plainly one:
 * two or more words of two letters or more, joined by dots, dashes or underscores
 * (`alex.morgan` reads "Alex Morgan"). Anything else is null, and the
 * email stands.
 */
export const nameFromEmail = (email: string): string | null => {
  const local = email.split("@")[0] ?? "";
  if (!/^[a-z]{2,}(?:[._-][a-z]{2,})+$/i.test(local)) return null;
  return local
    .split(/[._-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * The file's records: the server's, with their ids, or read back from the
 * lines of a server that sends only lines. Those carry no ids, so nothing
 * they list links anywhere.
 */
export const briefingItems = (file: ProbeBriefingFile): ProbeBriefingItems =>
  file.items ?? {
    findings: file.findings.map((line) => {
      const { severity, title, detail } = findingLine(line);
      return { id: null, severity, title, summary: detail };
    }),
    updates: file.updates.map((line) => {
      const { date, where, title } = updateLine(line);
      return { id: null, date, jurisdiction: where, title, url: null };
    }),
    authority: file.authority.map((line) => {
      const { jurisdiction, source, text } = authorityLine(line);
      return { jurisdiction: jurisdiction ?? "", source, text };
    }),
    library: file.library.map((line) => {
      const { name, facts } = libraryLine(line);
      return { id: null, name, facts: facts ?? "" };
    }),
  };
